import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import { useAuth } from "../../components/auth/AuthRequiredRoute/AuthRequiredRoute";
import ImageInput from "../../components/form/ImageInput/ImageInput";
import SelectInput from "../../components/form/SelectInput";
import Text from "../../components/typography/Text/Text";
import PrimaryButton from "../../components/ui/PrimaryButton";
import { DOCUMENT_NAMES } from "../../constants/documents";
import AdditionalContactCard from "../../features/loans/components/AdditionalContactCard";
import { useSearchInstallment } from "../../hooks/fetch-data/useSearchInstallment";
import { useFileUpload } from "../../hooks/useFileUpload";
import { aunjaiScService } from "../../services/aunjai-sc.service";
import { UpdateLoan } from "../../services/modules/loan";
import { syncFileNameWithBankName } from "../../utils/file";
import {
  convertFileInfoToAttachment,
  formatAttachment,
  getLoanList,
} from "../../utils/loan";
import {
  getDocumentRejectReason,
  getStatementDocuments,
  isDocumentRejected,
  isStatementDocumentRejected,
} from "../../utils/loan-document";
import SuccessSection from "./SuccessSection";
import styles from "./style.module.css";

import { Navigate } from "react-router-dom";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import HeaderComponent from "../Header/HeaderComponent";
import HeaderDesktop from "../Header/HeaderDesktop";

function getExampleImage(imageName) {
  return `/assets/img/ci/img/edit-loan/${imageName}.png`;
}

const EditMyLoanPage = () => {
  const { loanId } = useParams();
  const { t } = useTranslation("editLoan");
  const { userData } = useAuth();

  const BANK_NAME_OPTIONS = [
    { value: "kbank", label: t("bank1") },
    { value: "scb", label: t("bank2") },
    { value: "ktb", label: t("bank3") },
    { value: "bbl", label: t("bank4") },
    { value: "bay", label: t("bank5") },
  ];
  const { data: userLoanInfo, isFetching: isFetchingUserLoanInfo } =
    useSearchInstallment(userData?.id);

  const [additionalContacts, setAdditionalContacts] = useState([]);

  const [isSuccess, setIsSuccess] = useState(false);

  const loans = getLoanList(userLoanInfo);

  const {
    fileInfo: receiptEvidenceFileInfo,
    propagateFileChange: propagateReceiptEvidence,
  } = useFileUpload(DOCUMENT_NAMES.SALARY_RECEIPT);

  const { fileInfo: ncbFileInfo, propagateFileChange: propagateNcbFileChange } =
    useFileUpload(DOCUMENT_NAMES.NCB);

  const { propagateFileChange: propagateMouFileChange, fileInfo: mouFileInfo } =
    useFileUpload(DOCUMENT_NAMES.MOU);

  const {
    fileInfo: otherDocumentFileInfo,
    propagateFileChange: propagateOtherDocument,
  } = useFileUpload(DOCUMENT_NAMES.OTHER);

  const {
    fileInfo: bookBankgFileInfo,
    propagateFileChange: propagateBookBankFileChange,
    inputRef: bookBankgInputRef,
  } = useFileUpload(DOCUMENT_NAMES.BOOK_BANK);

  const {
    fileInfo: idCardFileInfo,
    propagateFileChange: propagateIdCardFileChange,
  } = useFileUpload(DOCUMENT_NAMES.ID_CARD);

  const {
    fileInfo: houseLicenseFileInfo,
    propagateFileChange: propagateHouseLicenseFileChange,
  } = useFileUpload(DOCUMENT_NAMES.HOUSE_LICENSE);

  const {
    fileInfo: passportFileInfo,
    propagateFileChange: propagatePassportFileChange,
  } = useFileUpload(DOCUMENT_NAMES.PASSPORT);

  const {
    fileInfo: visaFileInfo,
    propagateFileChange: propagateVisaFileChange,
  } = useFileUpload(DOCUMENT_NAMES.VISA);

  const {
    fileInfo: workPermitFileInfo,
    propagateFileChange: propagateWorkPermitFileChange,
  } = useFileUpload(DOCUMENT_NAMES.WORK_PERMIT);

  const [firstBankName, setFirstBankName] = useState("");

  const [secondBankName, setSecondBankName] = useState("");

  const [thirdBankName, setThirdBankName] = useState("");

  const loan = loans?.find((loan) => loan.loan_id === parseInt(loanId));

  const statementDocumentIds = getStatementDocuments(loan).map(
    (document) => document.document_id
  );

  const {
    fileInfo: firstInstallmentReceiptFileInfo,
    propagateFileChange: propagateFirstInstallmentReceipt,
    onSetFileInfo: onSetFirstInstallmentReceiptFileInfo,
  } = useFileUpload(
    `${DOCUMENT_NAMES.STATEMENT}${firstBankName}`,
    statementDocumentIds[0]
  );

  const {
    fileInfo: secondInstallmentReceiptFileInfo,
    propagateFileChange: propagateSecondInstallmentReceipt,
    onSetFileInfo: onSetSecondInstallmentReceiptFileInfo,
  } = useFileUpload(
    `${DOCUMENT_NAMES.STATEMENT}${secondBankName}`,
    statementDocumentIds[1]
  );

  const {
    fileInfo: thirdInstallmentReceiptFileInfo,
    propagateFileChange: propagateThirdInstallmentReceipt,
    onSetFileInfo: onSetThirdInstallmentReceiptFileInfo,
  } = useFileUpload(
    `${DOCUMENT_NAMES.STATEMENT}${thirdBankName}`,
    statementDocumentIds[2]
  );

  const navigate = useNavigate();

  const rejectedStatementDocuments = loan?.loan_documents.filter(
    (document) =>
      document.name.includes(DOCUMENT_NAMES.STATEMENT) &&
      document.review_status === "reject"
  );

  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (!loan) return;

    setAdditionalContacts(loan.additional_contacts);
  }, [loan]);

  async function handleSubmitDocuments() {
    try {
      const fileInfos = [
        firstInstallmentReceiptFileInfo,
        secondInstallmentReceiptFileInfo,
        thirdInstallmentReceiptFileInfo,
        ncbFileInfo,
        otherDocumentFileInfo,
        bookBankgFileInfo,
        idCardFileInfo,
        houseLicenseFileInfo,
        passportFileInfo,
        visaFileInfo,
        workPermitFileInfo,
        receiptEvidenceFileInfo,
        mouFileInfo,
      ];

      const uploadDocumentPromises = Promise.all(
        fileInfos.map((fileInfo) => {
          if (!fileInfo) return;

          const formattedFile = formatAttachment({
            loanId,
            attachment: convertFileInfoToAttachment(fileInfo),
          });

          return aunjaiScService.insertAttachment(formattedFile);
        })
      );

      await uploadDocumentPromises;

      // ⚠️ Prevents duplicated address
      delete loan.address;

      const hasRejectedAdditionalContact = loan?.additional_contacts?.some(
        (contact) => contact.status === "reject"
      );

      const payload = {
        ...loan,
        loan_state: "request",
      };

      // ⚠️ Prevents duplicated additional_contacts
      delete payload.additional_contacts;

      if (hasRejectedAdditionalContact) {
        payload.additional_contacts = additionalContacts;
      }

      await UpdateLoan({
        body: payload,
      });

      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      alert(t("alert"));
    }
  }

  if (isFetchingUserLoanInfo) return <LoadingPage message={t("loading")} />;

  if (!loan) {
    return <div>{t("notFound")}</div>;
  }

  if (loan.loan_state !== "document_reject") {
    return <Navigate to="/my-loans" />;
  }

  return (
    <div>
      {isDesktop ? (
        <HeaderDesktop title={t("header")} showIcon={true} onBack={true} />
      ) : (
        <HeaderComponent title={t("header")} onBack={true} />
      )}
      {isSuccess ? (
        <SuccessSection
          title={t("title")}
          descriptionLine1={t("descriptionLine1")}
          descriptionLine2={t("descriptionLine2")}
          onNext={() => navigate(`/my-loans`)}
        />
      ) : (
        <main className={styles.content}>
          {additionalContacts?.map(
            (contact, index) =>
              contact.status === "reject" && (
                <AdditionalContactCard
                  contact={contact}
                  index={index}
                  additionalContactSetter={setAdditionalContacts}
                  additionalContacts={additionalContacts}
                />
              )
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.ID_CARD) && (
            <ImageInput
              label={t("imageLabel")}
              description={t("imageDesc")}
              exampleImageUrl="/assets/img/ci/img/img-idcard.png"
              {...propagateIdCardFileChange()}
              fileInfo={idCardFileInfo}
              errorMessage={getDocumentRejectReason(
                loan,
                DOCUMENT_NAMES.ID_CARD
              )}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.MOU) && (
            <ImageInput
              label={t("image2Label")}
              description={t("image2Desc")}
              exampleImageUrl="/assets/img/ci/img/loan/example-mou.png"
              placeholder={t("image2Place")}
              required
              errorMessage={getDocumentRejectReason(loan, DOCUMENT_NAMES.MOU)}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.SALARY_RECEIPT) && (
            <ImageInput
              label={t("image3Label")}
              exampleImageUrl={getExampleImage("receipt-evidence-example")}
              description={t("image3Desc")}
              placeholder={t("image3Place")}
              errorMessage={getDocumentRejectReason(
                DOCUMENT_NAMES.SALARY_RECEIPT
              )}
              {...propagateReceiptEvidence()}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.OTHER) && (
            <ImageInput
              label={t("image4Label")}
              description={t("image4Desc")}
              placeholder={t("image4Place")}
              errorMessage={getDocumentRejectReason(loan, DOCUMENT_NAMES.OTHER)}
              {...propagateOtherDocument()}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.NCB) && (
            <ImageInput
              label={t("image5Label")}
              description={t("image5Desc")}
              placeholder={t("image5Place")}
              {...propagateNcbFileChange()}
              fileInfo={ncbFileInfo}
              accept="application/pdf"
              errorMessage={getDocumentRejectReason(loan, DOCUMENT_NAMES.NCB)}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.HOUSE_LICENSE) && (
            <ImageInput
              label={t("image6Label")}
              description={t("image6Desc")}
              placeholder={t("image6Place")}
              {...propagateHouseLicenseFileChange()}
              fileInfo={houseLicenseFileInfo}
              errorMessage={getDocumentRejectReason(
                loan,
                DOCUMENT_NAMES.HOUSE_LICENSE
              )}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.PASSPORT) && (
            <ImageInput
              label={t("image7Label")}
              description={t("image7Desc")}
              placeholder={t("image7Place")}
              {...propagatePassportFileChange()}
              fileInfo={passportFileInfo}
              errorMessage={getDocumentRejectReason(
                loan,
                DOCUMENT_NAMES.PASSPORT
              )}
            />
          )}
          {isDocumentRejected(loan, DOCUMENT_NAMES.VISA) && (
            <ImageInput
              label={t("image8Label")}
              description={t("image8Desc")}
              placeholder={t("image8Place")}
              {...propagateVisaFileChange()}
              fileInfo={visaFileInfo}
              errorMessage={getDocumentRejectReason(loan, DOCUMENT_NAMES.VISA)}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.WORK_PERMIT) && (
            <ImageInput
              label={t("image9Label")}
              description={t("imagimage9Desce8Label")}
              placeholder={t("image9Place")}
              {...propagateWorkPermitFileChange()}
              fileInfo={workPermitFileInfo}
              errorMessage={getDocumentRejectReason(
                loan,
                DOCUMENT_NAMES.WORK_PERMIT
              )}
            />
          )}

          {isDocumentRejected(loan, DOCUMENT_NAMES.BOOK_BANK) && (
            <ImageInput
              label={t("image10Label")}
              description={t("image10Desc")}
              placeholder={t("image10Place")}
              {...propagateBookBankFileChange()}
              fileInfo={bookBankgFileInfo}
              ref={bookBankgInputRef}
              errorMessage={getDocumentRejectReason(
                loan,
                DOCUMENT_NAMES.BOOK_BANK
              )}
            />
          )}

          {isStatementDocumentRejected(loan, 0) && (
            <div>
              <Text weight={800}>{t("uploadBank")}</Text>
              <SelectInput
                isFormik={false}
                label={t("bank")}
                placeholder={t("selectBank")}
                options={BANK_NAME_OPTIONS}
                value={firstBankName}
                onChange={async (e) => {
                  const bankName = e?.target?.value;

                  setFirstBankName(bankName);

                  await syncFileNameWithBankName(
                    bankName,
                    firstInstallmentReceiptFileInfo,
                    onSetFirstInstallmentReceiptFileInfo
                  );
                }}
              />
              <ImageInput
                description={t("image11Label")}
                placeholder={t("imag11Place")}
                errorMessage={getDocumentRejectReason(
                  loan,
                  DOCUMENT_NAMES.STATEMENT,
                  0
                )}
                {...propagateFirstInstallmentReceipt()}
                accept="application/pdf"
              />
            </div>
          )}

          {isStatementDocumentRejected(loan, 1) && (
            <div>
              <Text weight={800}>{t("uploadBank")}</Text>
              <SelectInput
                isFormik={false}
                label={t("bank")}
                placeholder={t("selectBank")}
                options={BANK_NAME_OPTIONS}
                value={secondBankName}
                onChange={async (e) => {
                  const bankName = e?.target?.value;

                  setSecondBankName(bankName);

                  await syncFileNameWithBankName(
                    bankName,
                    secondInstallmentReceiptFileInfo,
                    onSetSecondInstallmentReceiptFileInfo
                  );
                }}
              />
              <ImageInput
                description={t("image12Desc")}
                {...propagateSecondInstallmentReceipt()}
                accept="application/pdf"
                errorMessage={getDocumentRejectReason(
                  loan,
                  DOCUMENT_NAMES.STATEMENT,
                  1
                )}
              />
            </div>
          )}

          {isStatementDocumentRejected(loan, 2) && (
            <div>
              <Text weight={800}>{t("uploadBank")}</Text>
              <SelectInput
                isFormik={false}
                label={t("bank")}
                placeholder={t("selectBank")}
                options={BANK_NAME_OPTIONS}
                value={thirdBankName}
                onChange={async (e) => {
                  const bankName = e?.target?.value;

                  setThirdBankName(bankName);

                  await syncFileNameWithBankName(
                    bankName,
                    thirdInstallmentReceiptFileInfo,
                    onSetThirdInstallmentReceiptFileInfo
                  );
                }}
              />
              <ImageInput
                label={t("image13Label")}
                description={t("image13Desc")}
                {...propagateThirdInstallmentReceipt()}
                fileInfo={thirdInstallmentReceiptFileInfo}
                accept="application/pdf"
                errorMessage={getDocumentRejectReason(
                  loan,
                  DOCUMENT_NAMES.STATEMENT,
                  2
                )}
              />
            </div>
          )}

          <footer className={styles.footer}>
            <PrimaryButton onClick={handleSubmitDocuments}>
              {t("accept")}
            </PrimaryButton>
          </footer>
        </main>
      )}
    </div>
  );
};

export default EditMyLoanPage;
